import {
  Box,
  CircularProgress,
  FormControl,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import OutlinedInput from '@mui/material/OutlinedInput';
import { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';
import { toast } from 'react-toastify';

import Button from '@/components/Button';
import { COUNTRY_MEX, getCurrencyCode, getCurrencySymbol } from '@/constants/countries';
import { HttpContext } from '@/providers/HttpProvider/http-context';
import { getBalance } from '@/redux/parcel/asyncRequest';
import { RootState } from '@/redux/store';

import type { AppDispatch } from '@/redux/store';
import type { Countries } from '@/utils/common';

interface HowMuchRechargeProps {
  paymentMethod: string;
}

interface PriceOption {
  label: string;
  value: number;
}

const defaultPrices: PriceOption[] = [
  { label: '$ 200 MXN (Recomendado)', value: 200 },
  { label: '$ 300 MXN', value: 300 },
  { label: '$ 400 MXN', value: 400 },
  { label: '$ 500 MXN', value: 500 },
  { label: 'Otra cantidad', value: 0 },
];

const getDefaultPricesByCountry = (country: Countries): PriceOption[] => {
  const currencyCode = getCurrencyCode(country);
  const currencySymbol = getCurrencySymbol(country);

  if (country === 'PER') {
    return [
      { label: `${currencySymbol} 50 ${currencyCode} (Recomendado)`, value: 50 },
      { label: `${currencySymbol} 100 ${currencyCode}`, value: 100 },
      { label: `${currencySymbol} 150 ${currencyCode}`, value: 150 },
      { label: `${currencySymbol} 200 ${currencyCode}`, value: 200 },
      { label: 'Otra cantidad', value: 0 },
    ];
  } else if (country === 'COL') {
    return [
      { label: `${currencySymbol} 20,000 ${currencyCode} (Recomendado)`, value: 20000 },
      { label: `${currencySymbol} 40,000 ${currencyCode}`, value: 40000 },
      { label: `${currencySymbol} 60,000 ${currencyCode}`, value: 60000 },
      { label: `${currencySymbol} 80,000 ${currencyCode}`, value: 80000 },
      { label: 'Otra cantidad', value: 0 },
    ];
  } else if (country === 'CHL') {
    return [
      { label: `${currencySymbol} 10,000 ${currencyCode} (Recomendado)`, value: 10000 },
      { label: `${currencySymbol} 15,000 ${currencyCode}`, value: 15000 },
      { label: `${currencySymbol} 30,000 ${currencyCode}`, value: 30000 },
      { label: `${currencySymbol} 45,000 ${currencyCode}`, value: 45000 },
      { label: 'Otra cantidad', value: 0 },
    ];
  } else {
    // Default MEX
    return defaultPrices;
  }
};

const HowMuchRecharge = ({ paymentMethod }: HowMuchRechargeProps) => {
  const theme = useTheme();
  const { userData } = useContext(HttpContext);
  const { balance, profile } = useSelector((state: RootState) => state.parcel);
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedPrice, setSelectedPrice] = useState<string>('');
  const [customAmount, setCustomAmount] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [prices, setPrices] = useState<PriceOption[]>(defaultPrices);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [pricesError, setPricesError] = useState(false);

  const currentBalance = balance?.data || 0;
  const initialBalanceRef = useRef<number | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fetch Stripe prices when payment method is Stripe
  useEffect(() => {
    const fetchStripePrices = async () => {
      if (paymentMethod === 'card') {
        try {
          setLoadingPrices(true);
          setPricesError(false);
          const response = await userData.stripePrices();

          if (response.success && response.data && Array.isArray(response.data)) {
            const country: Countries = profile?.data?.country || COUNTRY_MEX;
            const currencyCode = getCurrencyCode(country);

            const sortedPrices = [...response.data].sort((a, b) => a - b);
            const formattedPrices: PriceOption[] = sortedPrices.map((price, index) => ({
              label:
                index === 0
                  ? `$ ${price} ${currencyCode} (Recomendado)`
                  : `$ ${price} ${currencyCode}`,
              value: price,
            }));

            // No agregar "Otra cantidad" para Stripe - REGLA DE NEGOCIO.
            setPrices(formattedPrices);
          } else {
            setPricesError(true);
            toast.error(
              'No se pudieron cargar los montos disponibles. Por favor intenta más tarde.',
            );
          }
        } catch (error) {
          console.error('Error fetching Stripe prices:', error);
          setPricesError(true);
          toast.error('Error al cargar los montos disponibles. Por favor intenta más tarde.');
        } finally {
          setLoadingPrices(false);
        }
      } else {
        // For MercadoPago, use country-specific default prices
        setPricesError(false);
        const country = profile?.data?.country || COUNTRY_MEX;
        setPrices(getDefaultPricesByCountry(country));
      }
    };

    fetchStripePrices();
  }, [paymentMethod, userData, profile?.data?.country]);

  // Detectar si el usuario regresó después del pago
  useEffect(() => {
    const paymentStatus = searchParams.get('payment_status');
    const paymentSuccess = searchParams.get('success');

    const isSuccess = paymentStatus === 'success' || paymentSuccess === 'true';
    const isFailure = paymentStatus === 'failure';
    const isPending = paymentStatus === 'pending';

    if (!isSuccess && !isFailure && !isPending) return;

    if (!profile.data) return;

    const isPrepaid = profile.data.configuration.paymentType === 'PREPAID';
    if (!isPrepaid) return;

    // Guardamos el balance antes del pago
    initialBalanceRef.current ??= currentBalance;

    // Siempre refrescamos balance al volver del checkout
    dispatch(getBalance());

    // ---- Mensajes según estatus ----
    if (isFailure) {
      // Failed
      toast.error('El pago no pudo ser procesado. No se realizó ningún cargo.');
    } else {
      // Pending (success o pending NO es Completed todavía)
      toast.info(
        'Pago recibido. Estamos confirmándolo con el proveedor; tu saldo se actualizará en breve.',
      );
    }

    // ---- Polling corto esperando webhook ----
    const MAX_ATTEMPTS = 6; // ~18 segundos
    let attempts = 0;

    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }

    pollingRef.current = globalThis.setInterval(() => {
      attempts += 1;

      // Solo consultar balance si es cuenta prepago
      if (profile.data?.configuration.paymentType === 'PREPAID') {
        dispatch(getBalance());
      }

      const initial = initialBalanceRef.current ?? currentBalance;

      // Completed → el balance ya se reflejó
      if (currentBalance > initial) {
        toast.success('¡Recarga aplicada correctamente!');
        clearInterval(pollingRef.current!);
        pollingRef.current = null;
        initialBalanceRef.current = null;
        return;
      }

      // Timeout: puede seguir Pending (webhook tardó)
      if (attempts >= MAX_ATTEMPTS) {
        clearInterval(pollingRef.current!);
        pollingRef.current = null;
        initialBalanceRef.current = null;

        toast.info(
          'Si tu saldo aún no se actualiza, el pago sigue en validación. Revisa nuevamente en unos minutos.',
        );
      }
    }, 3000);

    // ---- Limpieza de URL ----
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('payment_status');
    nextParams.delete('success');
    nextParams.delete('payment_id');
    nextParams.delete('preference_id');

    setSearchParams(nextParams, { replace: true });

    // Cleanup
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      initialBalanceRef.current = null;
    };
  }, [searchParams, dispatch, setSearchParams, currentBalance, profile.data]);

  const rechargeAmount =
    selectedPrice === 'Otra cantidad'
      ? customAmount
      : prices.find((p) => p.label === selectedPrice)?.value || 0;
  const finalBalance = currentBalance + rechargeAmount;

  const country: Countries = profile?.data?.country || COUNTRY_MEX;
  const currencyCode = getCurrencyCode(country);
  const currencySymbol = getCurrencySymbol(country);

  const rows = [
    {
      name: 'Tu saldo actual',
      value: `${currencySymbol} ${currentBalance.toFixed(2)} ${currencyCode}`,
    },
    { name: 'Tu recarga', value: `${currencySymbol} ${rechargeAmount.toFixed(2)} ${currencyCode}` },
    { name: 'Saldo final', value: `${currencySymbol} ${finalBalance.toFixed(2)} ${currencyCode}` },
  ];

  const handleRecharge = async () => {
    // Sanitización doble: asegura que la cantidad sea un entero puro
    const finalAmount = Math.floor(rechargeAmount);

    if (finalAmount <= 0) {
      toast.error('Por favor selecciona o ingresa un monto válido');
      return;
    }

    if (paymentMethod === 'mercadoPago') {
      try {
        setLoading(true);
        const response = await userData.mercadoPagoPaymentLink({ amount: finalAmount });

        if (response.success && response.data) {
          // Redirigir al link de pago de Mercado Pago
          globalThis.location.href = response.data;
        } else {
          toast.error(response.message || 'Error al generar el link de pago');
        }
      } catch (error) {
        console.error('Error al procesar el pago:', error);
        toast.error('Error al procesar el pago. Por favor intenta de nuevo.');
      } finally {
        setLoading(false);
      }
    } else if (paymentMethod === 'card') {
      try {
        setLoading(true);
        const response = await userData.stripePaymentLink({ amount: finalAmount });

        if (response.success && response.data) {
          // Redirigir al link de pago de Stripe
          globalThis.location.href = response.data;
        } else {
          toast.error(response.message || 'Error al generar el link de pago');
        }
      } catch (error) {
        console.error('Error al procesar el pago con Stripe:', error);
        toast.error('Error al procesar el pago. Por favor intenta de nuevo.');
      } finally {
        setLoading(false);
      }
    }
  };
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: '1rem',
      }}
    >
      <Typography
        variant="body1"
        color={theme.palette.secondary.main}
        sx={{ fontWeight: 'bold', '&& span': { color: theme.palette.primary.main } }}
      >
        ¿<span>Cuánto</span> necesitas recargar?
      </Typography>
      <FormControl sx={{ width: 200 }}>
        <Select
          displayEmpty
          value={selectedPrice}
          onChange={(e) => setSelectedPrice(e.target.value)}
          input={<OutlinedInput />}
          inputProps={{ 'aria-label': 'Without label' }}
          disabled={loadingPrices}
        >
          <MenuItem disabled value="">
            <em>{loadingPrices ? 'Cargando opciones...' : 'Selecciona una opción'}</em>
          </MenuItem>
          {prices.map((price) => (
            <MenuItem key={price.label} value={price.label}>
              {price.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {selectedPrice === 'Otra cantidad' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            Ingrese su cantidad
          </Typography>
          <TextField
            id="custom-amount"
            label=""
            variant="standard"
            type="number"
            value={customAmount === 0 ? '' : customAmount}
            onKeyDown={(e) => {
              // Previene ingresar punto, coma, signos negativo/positivo y notación científica
              if (['.', ',', '-', '+', 'e', 'E'].includes(e.key)) {
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              // Sanitiza lo pegado o ingresado forzando un entero base 10
              const parsed = parseInt(e.target.value, 10);
              setCustomAmount(isNaN(parsed) ? 0 : parsed);
            }}
            inputProps={{
              min: '1',
              step: '1',
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
            sx={{ width: '30%', '&& input': { textAlign: 'center' } }}
          />
        </Box>
      )}

      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
        Total:
      </Typography>
      <Paper sx={{ padding: '0.5rem 1rem', borderTop: `1px solid ${theme.palette.divider}` }}>
        {rows.map((row, index) => {
          const isFinal = index === rows.length - 1;
          return (
            <Box
              key={row.name}
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: '1rem',
                padding: isFinal ? '0.75rem 0 0.25rem' : '0.625rem 0',
                borderBottom: isFinal ? 'none' : `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: isFinal ? '0.95rem' : '0.875rem',
                  fontWeight: isFinal ? 700 : 500,
                  color: isFinal ? theme.palette.secondary.main : theme.palette.text.secondary,
                }}
              >
                {row.name}
              </Typography>
              <Typography
                sx={{
                  fontSize: isFinal ? '1.15rem' : '0.9rem',
                  fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                  color: isFinal ? theme.palette.primary.main : theme.palette.secondary.main,
                }}
              >
                {row.value}
              </Typography>
            </Box>
          );
        })}
      </Paper>

      <Button
        variant="contained"
        onClick={handleRecharge}
        disabled={
          loading ||
          rechargeAmount <= 0 ||
          (paymentMethod === 'card' && pricesError) ||
          loadingPrices
        }
        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
        sx={{ width: '90%', alignSelf: 'center' }}
      >
        {(() => {
          if (loading) return 'Procesando...';
          if (pricesError && paymentMethod === 'card') return 'No disponible';
          return 'Recargar';
        })()}
      </Button>
    </Box>
  );
};

export default HowMuchRecharge;
